import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Pencil, Globe, ChevronRight, ArrowLeft, Leaf, Package } from "lucide-react";
import { toast } from "sonner";
import { FLOWER_TIERS, FLOWER_WEIGHTS, WEIGHT_GRAMS, TIER_NAMES, TIER_COLORS } from "@/lib/flowerTiers";

const EMPTY = { name: "", brand: "", category: "", description: "", strain_type: "", thc: 0, cbd: 0, thca: 0, d9: 0, image_url: "", barcode: "", sku: "", cost: 0, price: 0, inventory_qty: 0, active: true, publish_to_website: false, variants: [] };
const STRAIN_TYPES = ["Indica", "Sativa", "Hybrid"];
const isFlowerCat = (c) => (c?.name || "").toLowerCase() === "flower";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState(null); // active category (Manage Products view)
  const [catDialog, setCatDialog] = useState(null); // {id?, name} being added/edited

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const delCat = (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    api.delete(`/categories/${c.id}`).then(() => { toast.success("Category deleted"); load(); }).catch((e) => toast.error(formatApiError(e)));
  };

  if (active) {
    const cat = categories.find((c) => c.id === active.id) || active;
    return (
      <div className="max-w-6xl mx-auto">
        <button onClick={() => setActive(null)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="inventory-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {isFlowerCat(cat)
          ? <FlowerManager reloadParent={load} />
          : <ManageProducts category={cat} products={products.filter((p) => (p.category || "").toLowerCase() === cat.name.toLowerCase())} categories={categories} reload={load} />}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Inventory Management</h1>
          <p className="text-sm text-slate-500 mt-1">Select a category to manage products</p>
        </div>
        <Button onClick={() => setCatDialog({ name: "" })} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Category
        </Button>
      </div>

      {categories.length === 0 ? (
        <div className="p-12 text-center text-slate-400 text-sm">No categories yet. Add one to get started.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {categories.map((c) => {
            const count = products.filter((p) => (p.category || "").toLowerCase() === c.name.toLowerCase()).length;
            return (
              <Card key={c.id} className="relative p-5 flex flex-col items-center text-center border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all" data-testid={`category-card-${c.id}`}>
                <div className="absolute top-2 right-2 flex gap-1">
                  <button onClick={() => setCatDialog({ id: c.id, name: c.name })} className="p-1.5 text-slate-400 hover:text-indigo-600" data-testid={`edit-category-${c.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => delCat(c)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`delete-category-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-3 ${isFlowerCat(c) ? "bg-emerald-50" : "bg-indigo-50"}`}>
                  {isFlowerCat(c) ? <Leaf className="w-8 h-8 text-emerald-600" /> : <Package className="w-8 h-8 text-indigo-600" />}
                </div>
                <div className="font-bold text-lg text-slate-900">{c.name}</div>
                <div className="text-xs text-slate-400 mt-0.5 mb-4">{count} {count === 1 ? "product" : "products"}</div>
                <Button onClick={() => setActive(c)} className="mt-auto w-full bg-indigo-600 hover:bg-indigo-700" data-testid={`manage-category-${c.id}`}>Manage Products</Button>
              </Card>
            );
          })}
        </div>
      )}

      {catDialog && <CategoryDialog cat={catDialog} onClose={() => setCatDialog(null)} onSaved={() => { setCatDialog(null); load(); }} />}
    </div>
  );
}

function CategoryDialog({ cat, onClose, onSaved }) {
  const [name, setName] = useState(cat.name || "");
  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    try {
      if (cat.id) await api.patch(`/categories/${cat.id}`, { name: name.trim() });
      else await api.post("/categories", { name: name.trim() });
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{cat.id ? "Edit" : "Add"} category</DialogTitle></DialogHeader>
        <Field label="Category name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Edibles" data-testid="category-name-input" autoFocus /></Field>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} className="bg-indigo-600" data-testid="save-category-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageProducts({ category, products, categories, reload }) {
  const [editing, setEditing] = useState(null);
  const toggle = (p) => api.post(`/products/${p.id}/publish-toggle`).then(() => { toast.success("Website publish toggled"); reload(); }).catch((e) => toast.error(formatApiError(e)));
  const del = (id) => { if (!window.confirm("Delete this product?")) return; api.delete(`/products/${id}`).then(() => { toast.success("Deleted"); reload(); }); };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{category.name}</h2>
        <Button onClick={() => setEditing({ ...EMPTY, category: category.name })} className="bg-indigo-600" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1.5" />New product</Button>
      </div>
      <Card className="divide-y">
        {products.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`product-row-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-800 flex items-center gap-2">{p.name}
                {p.publish_to_website && <Badge className="bg-blue-100 text-blue-700"><Globe className="w-3 h-3 mr-0.5" />Web</Badge>}
                {p.active === false && <Badge variant="secondary">Inactive</Badge>}
              </div>
              <div className="text-xs text-slate-500">{p.brand ? `${p.brand} · ` : ""}{(p.variants || []).length} variants</div>
            </div>
            <button onClick={() => toggle(p)} className="text-xs text-slate-500 hover:text-blue-600" data-testid={`publish-toggle-${p.id}`}>{p.publish_to_website ? "Unpublish" : "Publish"}</button>
            <Button variant="ghost" size="icon" onClick={() => setEditing(p)} data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => del(p.id)} data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
          </div>
        ))}
        {products.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No products in {category.name} yet.</div>}
      </Card>
      {editing && <ProductDialog product={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function ProductDialog({ product, categories, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...product, variants: product.variants || [] });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const addVariant = () => set("variants", [...f.variants, { label: "", price: 0, cost: 0, inventory_qty: 0, sku: "", barcode: "", weight_grams: 0, active: true }]);
  const setV = (i, k, v) => set("variants", f.variants.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const rmV = (i) => set("variants", f.variants.filter((_, idx) => idx !== i));

  const save = async () => {
    const payload = { ...f, thc: +f.thc, cbd: +f.cbd, thca: +f.thca, d9: +f.d9, cost: +f.cost, price: +f.price, inventory_qty: +f.inventory_qty,
      variants: f.variants.map((v) => ({ ...v, price: +v.price, cost: +v.cost, inventory_qty: +v.inventory_qty, weight_grams: +v.weight_grams })) };
    try {
      if (product.id) await api.patch(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>{product.id ? "Edit" : "New"} product</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" /></Field>
          <Field label="Brand"><Input value={f.brand} onChange={(e) => set("brand", e.target.value)} data-testid="product-brand-input" /></Field>
          <Field label="Category">
            <Select value={f.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{categories.filter((c) => c.name.toLowerCase() !== "flower").map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Strain type"><Input value={f.strain_type} onChange={(e) => set("strain_type", e.target.value)} placeholder="Indica/Sativa/Hybrid" data-testid="product-strain-input" /></Field>
          <Field label="THC %"><Input type="number" value={f.thc} onChange={(e) => set("thc", e.target.value)} /></Field>
          <Field label="THCA %"><Input type="number" value={f.thca} onChange={(e) => set("thca", e.target.value)} /></Field>
          <Field label="Cost $"><Input type="number" value={f.cost} onChange={(e) => set("cost", e.target.value)} data-testid="product-cost-input" /></Field>
          <Field label="Retail $"><Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="product-price-input" /></Field>
          <Field label="Base inventory"><Input type="number" value={f.inventory_qty} onChange={(e) => set("inventory_qty", e.target.value)} data-testid="product-inventory-input" /></Field>
          <Field label="Barcode / SKU"><Input value={f.barcode} onChange={(e) => set("barcode", e.target.value)} data-testid="product-barcode-input" /></Field>
          <Field label="Image URL"><Input value={f.image_url} onChange={(e) => set("image_url", e.target.value)} /></Field>
        </div>
        <Field label="Description"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} /></Field>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="product-active-switch" /> Active</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.publish_to_website} onCheckedChange={(v) => set("publish_to_website", v)} data-testid="product-publish-switch" /> Publish to website</label>
        </div>
        <div className="border-t pt-3">
          <div className="flex justify-between items-center mb-2"><span className="font-medium text-sm">Variants (SKUs)</span><Button size="sm" variant="outline" onClick={addVariant} data-testid="add-variant-btn"><Plus className="w-4 h-4 mr-1" />Variant</Button></div>
          <div className="space-y-2">
            {f.variants.map((v, i) => (
              <div key={i} className="grid grid-cols-5 gap-2 items-center" data-testid={`variant-row-${i}`}>
                <Input placeholder="Label" value={v.label} onChange={(e) => setV(i, "label", e.target.value)} data-testid={`variant-label-${i}`} />
                <Input placeholder="Price" type="number" value={v.price} onChange={(e) => setV(i, "price", e.target.value)} data-testid={`variant-price-${i}`} />
                <Input placeholder="Cost" type="number" value={v.cost} onChange={(e) => setV(i, "cost", e.target.value)} />
                <Input placeholder="Qty" type="number" value={v.inventory_qty} onChange={(e) => setV(i, "inventory_qty", e.target.value)} data-testid={`variant-qty-${i}`} />
                <div className="flex gap-1"><Input placeholder="Barcode" value={v.barcode} onChange={(e) => setV(i, "barcode", e.target.value)} /><Button variant="ghost" size="icon" onClick={() => rmV(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-product-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>
);

function FlowerManager({ reloadParent }) {
  const [strains, setStrains] = useState([]);
  const [view, setView] = useState("tiers");
  const [tier, setTier] = useState(null);
  const [sub, setSub] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editStrain, setEditStrain] = useState(null);

  const load = useCallback(() => { api.get("/flower-strains").then((r) => setStrains(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const pickTier = (t) => {
    const subs = Object.keys(FLOWER_TIERS[t]);
    setTier(t);
    if (subs.length === 1) { setSub(subs[0]); setView("strains"); }
    else { setSub(null); setView("subtiers"); }
  };
  const visible = strains.filter((st) => st.brand === `${tier} - ${sub}`);
  const del = (id) => { if (!window.confirm("Delete this strain?")) return; api.delete(`/products/${id}`).then(() => { toast.success("Deleted"); load(); reloadParent(); }); };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        {view !== "tiers" && (
          <button onClick={() => { if (view === "strains" && Object.keys(FLOWER_TIERS[tier]).length > 1) setView("subtiers"); else { setView("tiers"); setTier(null); setSub(null); } }}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="flower-back-btn"><ArrowLeft className="w-4 h-4" />Back</button>
        )}
        <div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            {view === "tiers" ? "Flower Inventory – Select Tier" : view === "subtiers" ? `${tier} – Select Price` : `${tier} ${sub} · Strains`}
          </h2>
          {view === "tiers" && <p className="text-sm text-slate-500">Select a pricing tier</p>}
        </div>
      </div>

      {view === "tiers" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {TIER_NAMES.map((t) => (
            <button key={t} onClick={() => pickTier(t)} data-testid={`flower-tier-${t}`}
              className="rounded-2xl border-2 border-slate-200 bg-white p-4 hover:border-indigo-400 hover:shadow-md transition-all flex flex-col items-center">
              <div className={`w-full rounded-xl py-6 flex flex-col items-center text-white bg-gradient-to-br ${TIER_COLORS[t]}`}>
                <Leaf className="w-8 h-8 mb-2 opacity-90" />
                <div className="text-xl font-bold">{t}</div>
              </div>
              <div className="w-full mt-3 space-y-1.5">
                {Object.keys(FLOWER_TIERS[t]).map((s) => (
                  <div key={s} className="text-center text-sm font-semibold text-slate-700 bg-slate-50 rounded-md py-1.5">{s}</div>
                ))}
              </div>
              <div className="text-xs text-slate-400 mt-2">{strains.filter((s) => s.tier === t).length} strains</div>
            </button>
          ))}
        </div>
      )}

      {view === "subtiers" && tier && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.keys(FLOWER_TIERS[tier]).map((s) => (
            <button key={s} onClick={() => { setSub(s); setView("strains"); }} data-testid={`flower-subtier-${s}`}
              className="rounded-xl border-2 border-slate-200 bg-white p-5 text-left hover:border-indigo-400 flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-800">{tier} {s}</div>
                <div className="text-xs text-slate-500 mt-1">1G {money(FLOWER_TIERS[tier][s]["1G"])} · 28G {money(FLOWER_TIERS[tier][s]["28G"])}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </button>
          ))}
        </div>
      )}

      {view === "strains" && (
        <div className="space-y-3">
          <div className="flex items-center justify-end">
            <Button onClick={() => setAdding(true)} className="bg-indigo-600" data-testid="add-strain-btn"><Plus className="w-4 h-4 mr-1.5" />Add Strain</Button>
          </div>
          <Card className="divide-y">
            {visible.map((st) => (
              <div key={st.id} className="flex items-center gap-3 p-3" data-testid={`strain-row-${st.id}`}>
                <div className="flex-1">
                  <div className="font-medium text-slate-800 flex items-center gap-2">{st.name}
                    {st.publish_to_website && <Badge className="bg-blue-100 text-blue-700"><Globe className="w-3 h-3 mr-0.5" />Web</Badge>}
                    {st.strain_type && <Badge variant="secondary" className="capitalize">{st.strain_type}</Badge>}
                  </div>
                  <div className="text-xs text-slate-500">{(st.grams || 0).toFixed(1)}g on hand · THCa {st.thca || 0}% · Δ9 {st.d9 || 0}% · ${st.cost_per_gram || 0}/g cost</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setEditStrain(st)} data-testid={`edit-strain-${st.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(st.id)} data-testid={`delete-strain-${st.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            ))}
            {visible.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No strains in this tier yet. Add one.</div>}
          </Card>
        </div>
      )}

      {(adding || editStrain) && <AddStrainDialog tier={tier} sub={sub} strain={editStrain}
        onClose={() => { setAdding(false); setEditStrain(null); }}
        onSaved={() => { setAdding(false); setEditStrain(null); load(); reloadParent(); }} />}
    </div>
  );
}

function AddStrainDialog({ tier, sub, strain, onClose, onSaved }) {
  const [f, setF] = useState(strain ? {
    name: strain.name || "", strain_type: strain.strain_type || "", thca: strain.thca || 0, d9: strain.d9 || 0,
    description: strain.description || "", total_grams: strain.grams || 0, cost_per_gram: strain.cost_per_gram || 0,
    publish_to_website: !!strain.publish_to_website,
  } : { name: "", strain_type: "", thca: 0, d9: 0, description: "", total_grams: 0, cost_per_gram: 0, publish_to_website: false });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const prices = FLOWER_TIERS[tier]?.[sub] || {};
  const cpg = +f.cost_per_gram || 0;
  const save = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    const payload = { name: f.name, category: "Flower", tier, sub_tier: sub, strain_type: f.strain_type,
      thca: +f.thca, d9: +f.d9, description: f.description, cost_per_gram: +f.cost_per_gram,
      total_grams: +f.total_grams, publish_to_website: f.publish_to_website };
    try {
      if (strain) await api.patch(`/products/${strain.id}`, payload); else await api.post("/products", payload);
      toast.success("Strain saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>{strain ? "Edit" : "Add"} Strain</DialogTitle></DialogHeader>

        <Field label="Strain Name *"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="strain-name-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="THCA % *"><Input type="number" value={f.thca} onChange={(e) => set("thca", e.target.value)} data-testid="strain-thca-input" /></Field>
          <Field label="D9 %"><Input type="number" value={f.d9} onChange={(e) => set("d9", e.target.value)} placeholder="e.g., 0.8" data-testid="strain-d9-input" /></Field>
        </div>
        <Field label="Strain Type *">
          <Select value={f.strain_type} onValueChange={(v) => set("strain_type", v)}>
            <SelectTrigger data-testid="strain-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>{STRAIN_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>

        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
          <div className="text-sm font-semibold text-slate-800">{tier} - {sub}</div>
          <div className="text-xs text-slate-500 mb-2">Auto-Set Prices:</div>
          <div className="grid grid-cols-5 gap-2">
            {FLOWER_WEIGHTS.map((w) => (
              <div key={w} className="text-center bg-white rounded-md border border-slate-200 py-2">
                <div className="text-xs font-semibold text-slate-700">{w}</div>
                <div className="text-sm font-bold text-emerald-600">{money(prices[w] || 0)}</div>
              </div>
            ))}
          </div>
        </div>

        <Field label="Description (Optional)"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} placeholder="Notes about this strain..." /></Field>

        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-3 space-y-1">
          <Label className="text-sm font-semibold text-slate-800">Total Grams for This Location *</Label>
          <Input type="number" value={f.total_grams} onChange={(e) => set("total_grams", e.target.value)} data-testid="strain-grams-input" />
          <p className="text-xs text-slate-500">💡 Enter the total grams available for this strain. When you sell 1G, 4G, 8G, etc., it will automatically deduct from this total.</p>
        </div>

        <div className="rounded-xl border-2 border-amber-200 bg-amber-50/50 p-3 space-y-2">
          <Label className="text-sm font-semibold text-slate-800">Cost Per Gram (What You Pay) *</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
            <Input type="number" value={f.cost_per_gram} onChange={(e) => set("cost_per_gram", e.target.value)} className="pl-6" data-testid="strain-cost-input" />
          </div>
          <p className="text-xs text-slate-500">💰 Enter your wholesale cost per gram. This will be used to calculate profit margins for analytics.</p>
          <div className="bg-white rounded-lg border border-slate-200 p-2">
            <div className="text-xs font-semibold text-slate-700 mb-1.5">Calculated Costs per Weight:</div>
            <div className="grid grid-cols-5 gap-2">
              {FLOWER_WEIGHTS.map((w) => {
                const g = WEIGHT_GRAMS[w];
                const cost = cpg * g;
                const profit = (prices[w] || 0) - cost;
                return (
                  <div key={w} className="text-center" data-testid={`strain-costcalc-${w}`}>
                    <div className="text-xs font-semibold text-slate-700">{w}</div>
                    <div className="text-[11px] text-red-500">Cost: {money(cost)}</div>
                    <div className="text-[11px] text-emerald-600">Profit: {money(profit)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm"><Switch checked={f.publish_to_website} onCheckedChange={(v) => set("publish_to_website", v)} data-testid="strain-publish-switch" /> Publish to website</label>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} className="bg-indigo-600" data-testid="save-strain-btn">Save strain</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
